import { usePurchaseOrders } from '@round/creatorbase-helpers/api-hooks';
import uniq from 'lodash/uniq';
import { useMemo } from 'react';
import { creatorbaseQueries } from '@round/api';
import { useQueries } from '@tanstack/react-query';

type Params = { campaignIds: number[] };
type Config = { enabled?: boolean };

export default function useCampaignPurchaseOrders({ campaignIds }: Params, config: Config = { enabled: true }) {
    const {
        lines,
        isLoading: areLinesLoading,
        isFetching: areLinesFetching,
    } = usePurchaseOrderLinesForCampaigns(campaignIds, config);

    const purchaseOrderIds = uniq(lines.map((line) => line.purchase_order_id));
    const purchaseOrders = usePurchaseOrders(
        { id: purchaseOrderIds.toString(), page_size: purchaseOrderIds.length },
        { enabled: !!purchaseOrderIds.length && config.enabled }
    );

    return {
        data: useMemo(
            () =>
                lines.map((line) => {
                    return {
                        campaignId: line.campaign_id,
                        purchaseOrder:
                            purchaseOrders.data?.results.find((po) => po.id === line.purchase_order_id) ?? null,
                    };
                }),
            [lines, purchaseOrders.data?.results]
        ),
        isLoading: purchaseOrders.isLoading || areLinesLoading,
        isFetching: purchaseOrders.isFetching || areLinesFetching,
    };
}

const poLineOptions = creatorbaseQueries.purchaseOrderLines.options.listAll;

function usePurchaseOrderLinesForCampaigns(campaignIds: number[], config: Config = { enabled: true }) {
    return useQueries({
        queries: campaignIds.map((id) => ({
            ...poLineOptions({ campaign_id: id.toString(), max_items_per_po: 1 }),
            ...config,
        })),
        combine: (queries) => {
            return {
                lines: queries.flatMap((query) => query.data ?? []),
                isLoading: queries.some((query) => query.isLoading),
                isFetching: queries.some((query) => query.isFetching),
            };
        },
    });
}
